import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiUrl } from '../lib/api';
import { ssoStartUrl } from '../lib/standaloneSso';
import { getDefaultPortalPath } from '../lib/portalAccess';
import { LOGO_SRC } from '../data/logo';
import { ArrowRight, LogIn, ShieldCheck, Eye, EyeOff, Monitor, Users } from 'lucide-react';

export default function LoginPage() {
  const [espace, setEspace] = useState('tech');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loadingLocal, setLoadingLocal] = useState(false);
  const { loginLocal, isAuthenticated, loading, providers, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const loginSearchParams = new URLSearchParams(location.search);
  const debugMode = loginSearchParams.get('debug') === '1' || loginSearchParams.get('config') === '1';
  const showLocalFallback = providers.localEnabled && debugMode;

  useEffect(() => { document.title = 'Connexion'; }, []);

  useEffect(() => {
    // URLSearchParams.get décode déjà : un second decodeURIComponent levait
    // URIError (écran blanc) dès qu'un message contenait « % » mal formé.
    const incomingError = new URLSearchParams(location.search).get('error');
    setError(incomingError || '');
  }, [location.search]);

  const isBiHost = typeof window !== 'undefined' && window.location.hostname === 'bi.meteris.fr';
  const isRcHost = typeof window !== 'undefined' && window.location.hostname === 'rc.meteris.fr';

  // Depuis bi/rc, le SSO doit démarrer sur le portail : c'est lui qui recevra le
  // retour de Microsoft, donc lui qui doit détenir le cookie d'état anti-CSRF.
  // L'URL revient absolue dans ce cas — `apiUrl` ne s'applique qu'aux relatives.
  const ssoHref = (path, esp) => {
    const url = ssoStartUrl(path, {
      hostname: typeof window !== 'undefined' ? window.location.hostname : '',
      espace: esp,
    });
    return url.startsWith('http') ? url : apiUrl(url);
  };

  useEffect(() => {
    if (!loading && isAuthenticated) {
      const returnTo = new URLSearchParams(location.search).get('returnTo');
      const dest = isBiHost ? '/bi' : isRcHost ? '/reception' : (returnTo || getDefaultPortalPath(user));
      navigate(dest, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, user]);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoadingLocal(true);
    setError('');
    try {
      const authenticatedUser = await loginLocal(username, password);
      navigate(isBiHost ? '/bi' : getDefaultPortalPath(authenticatedUser), { replace: true });
    } catch (err) {
      setError(err.message || 'Erreur de connexion au serveur');
    } finally {
      setLoadingLocal(false);
    }
  }

  const hasSso = providers.microsoftEnabled || providers.googleEnabled;

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadingCardStyle} className="modal-animate">
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Chargement</div>
          <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>
            Verification de la configuration de connexion...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={outerCardStyle} className="modal-animate">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <img src={LOGO_SRC} alt="METERIS" style={{ height: 36 }} />
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, letterSpacing: 2, fontFamily: "'IBM Plex Mono', monospace" }}>METERIS</div>
            <div style={{ color: '#0099D6', fontSize: 10, letterSpacing: 1.5, fontFamily: "'IBM Plex Mono', monospace" }}>PORTAIL DE CONNEXION</div>
          </div>
        </div>

        {/* ── Espace selector tabs ── */}
        <div style={tabsRowStyle}>
          <button
            style={espace === 'tech' ? tabActiveStyle : tabStyle}
            onClick={() => setEspace('tech')}
          >
            <Monitor size={16} />
            Espace Meteris
          </button>
          <button
            style={espace === 'client' ? { ...tabActiveStyle, ...tabClientActiveOverride } : tabStyle}
            onClick={() => setEspace('client')}
          >
            <Users size={16} />
            Espace Client
          </button>
        </div>

        {/* ── Espace content ── */}
        <div style={espaceContentStyle}>
          {espace === 'tech' && (
            <>
              <p style={espaceDescStyle}>
                Connexion reservee aux techniciens et administrateurs METERIS.
              </p>

              {/* SSO buttons */}
              {providers.microsoftEnabled && (
                <SsoButton
                  href={ssoHref(providers.microsoftLoginUrl || '/api/auth/microsoft/start', 'tech')}
                  icon={<MicrosoftLogo />}
                  label="Microsoft 365"
                  sublabel="Se connecter avec Microsoft"
                  gradient="linear-gradient(135deg, #0f6cbd 0%, #115ea3 100%)"
                  shadow="rgba(17,94,163,0.28)"
                  shadowHover="rgba(17,94,163,0.42)"
                />
              )}

              {providers.googleEnabled && (
                <SsoButton
                  href={ssoHref(providers.googleLoginUrl || '/api/auth/google/start', 'tech')}
                  icon={<GoogleLogo />}
                  label="Google"
                  sublabel="Se connecter avec Google"
                  gradient="linear-gradient(135deg, #4285f4 0%, #3367d6 100%)"
                  shadow="rgba(66,133,244,0.22)"
                  shadowHover="rgba(66,133,244,0.38)"
                />
              )}

              {!hasSso && (
                <div style={warningBoxStyle}>
                  Aucun fournisseur SSO n&apos;est configure. Renseignez les variables Microsoft ou Google cote serveur.
                </div>
              )}

              {/* Status pills */}
              <div style={statusRowStyle}>
                <StatusPill label="Microsoft" active={providers.microsoftEnabled} />
                <StatusPill label="Google" active={providers.googleEnabled} />
              </div>
            </>
          )}

          {espace === 'client' && (
            <>
              <p style={espaceDescStyle}>
                Acces pour les clients et partenaires externes METERIS.
              </p>

              {providers.googleEnabled && (
                <SsoButton
                  href={ssoHref(providers.googleLoginUrl || '/api/auth/google/start', 'client')}
                  icon={<GoogleLogo />}
                  label="Google"
                  sublabel="Se connecter avec Google"
                  gradient="linear-gradient(135deg, #4285f4 0%, #3367d6 100%)"
                  shadow="rgba(66,133,244,0.22)"
                  shadowHover="rgba(66,133,244,0.38)"
                />
              )}

              {providers.microsoftEnabled && (
                <SsoButton
                  href={ssoHref(providers.microsoftLoginUrl || '/api/auth/microsoft/start', 'client')}
                  icon={<MicrosoftLogo />}
                  label="Microsoft 365"
                  sublabel="Se connecter avec Microsoft"
                  gradient="linear-gradient(135deg, #0f6cbd 0%, #115ea3 100%)"
                  shadow="rgba(17,94,163,0.28)"
                  shadowHover="rgba(17,94,163,0.42)"
                />
              )}

              {!hasSso && (
                <div style={warningBoxStyle}>
                  Aucun fournisseur SSO n&apos;est configure pour le moment.
                </div>
              )}

              <div style={infoBoxStyle}>
                Pas encore de compte ? Connectez-vous pour creer votre demande d&apos;acces.
                Un administrateur validera votre inscription.
              </div>
            </>
          )}
        </div>

        {/* ── Local fallback (debug only) ── */}
        {showLocalFallback && (
          <>
            <div style={{ ...separatorStyle, marginTop: 22 }}>
              <div style={separatorLineStyle} />
              <span>DEBUG</span>
              <div style={separatorLineStyle} />
            </div>

            <div style={debugBoxStyle}>
              Mode debug actif : la connexion locale de secours reste disponible sans etre exposee aux utilisateurs finaux.
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Identifiant</label>
                <input
                  data-testid="login-username"
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Mot de passe</label>
                <div style={{ position: 'relative' }}>
                  <input
                    data-testid="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    style={{ ...inputStyle, paddingRight: 46 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(current => !current)}
                    style={eyeBtnStyle}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                data-testid="login-submit"
                type="submit"
                disabled={loadingLocal}
                style={{
                  ...submitBtnStyle,
                  background: loadingLocal ? 'rgba(0,153,214,0.5)' : '#1e293b',
                  cursor: loadingLocal ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={e => { if (!loadingLocal) e.currentTarget.style.background = '#253448'; }}
                onMouseLeave={e => { if (!loadingLocal) e.currentTarget.style.background = '#1e293b'; }}
              >
                <LogIn size={16} />
                {loadingLocal ? 'Connexion...' : 'Connexion locale'}
              </button>
            </form>
          </>
        )}

        {error && (
          <div data-testid="login-error" className="dropdown-animate" style={errorBoxStyle}>
            {error}
          </div>
        )}

        <div style={footerStyle}>
          <ShieldCheck size={14} />
          <span>Les acces sont controles par roles et permissions.</span>
        </div>

        <div style={{ marginTop: 22, textAlign: 'center' }}>
          <Link
            to="/"
            style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textDecoration: 'none', transition: 'color 0.15s ease' }}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}
          >
            ← Retour a l&apos;accueil
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ── */

const MicrosoftLogo = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const GoogleLogo = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

function SsoButton({ href, label, sublabel, gradient, shadow, shadowHover, icon }) {
  return (
    <a
      href={href}
      style={{ ...ssoBtnBaseStyle, background: gradient, boxShadow: `0 16px 36px ${shadow}` }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 22px 48px ${shadowHover}`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = `0 16px 36px ${shadow}`; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={ssoIconWrapStyle}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.7 }}>
            {label}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 3 }}>
            {sublabel}
          </div>
        </div>
      </div>
      <ArrowRight size={18} />
    </a>
  );
}

function StatusPill({ label, active }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 10,
      color: active ? '#47c5fb' : 'rgba(255,255,255,0.3)',
      letterSpacing: 0.5,
    }}>
      <div style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: active ? '#47c5fb' : 'rgba(255,255,255,0.2)',
      }} />
      {label}: {active ? 'Actif' : 'Inactif'}
    </div>
  );
}

/* ── Styles ── */

const pageStyle = {
  minHeight: '100vh',
  background: 'radial-gradient(ellipse at 30% 20%, #111827 0%, #0B0C10 70%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
  padding: '24px',
};

const loadingCardStyle = {
  width: '100%',
  maxWidth: 430,
  background: '#15171E',
  border: '1px solid rgba(0,153,214,0.15)',
  borderRadius: 18,
  padding: '42px 36px',
  boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
};

const outerCardStyle = {
  width: '100%',
  maxWidth: 480,
  background: '#15171E',
  border: '1px solid rgba(0,153,214,0.15)',
  borderRadius: 18,
  padding: '42px 36px',
  boxShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 120px rgba(0,153,214,0.04)',
};

/* ── Tabs ── */
const tabsRowStyle = {
  display: 'flex',
  gap: 0,
  marginBottom: 0,
  borderRadius: '12px 12px 0 0',
  overflow: 'hidden',
  border: '1px solid rgba(255,255,255,0.08)',
  borderBottom: 'none',
};

const tabStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '14px 16px',
  background: 'rgba(255,255,255,0.02)',
  border: 'none',
  color: 'rgba(255,255,255,0.35)',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "'IBM Plex Mono', monospace",
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  letterSpacing: 0.3,
};

const tabActiveStyle = {
  ...tabStyle,
  background: 'rgba(0,153,214,0.12)',
  color: '#47c5fb',
  borderBottom: '2px solid #47c5fb',
  cursor: 'default',
};

const tabClientActiveOverride = {
  background: 'rgba(74,222,128,0.08)',
  color: '#4ade80',
  borderBottom: '2px solid #4ade80',
};

/* ── Espace content ── */
const espaceContentStyle = {
  background: 'rgba(255,255,255,0.02)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderTop: 'none',
  borderRadius: '0 0 12px 12px',
  padding: '20px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  marginBottom: 4,
};

const espaceDescStyle = {
  color: 'rgba(255,255,255,0.45)',
  fontSize: 12,
  lineHeight: 1.6,
  margin: '0 0 4px 0',
};

/* ── SSO button ── */
const ssoIconWrapStyle = {
  width: 36,
  height: 36,
  background: 'rgba(255,255,255,0.15)',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const ssoBtnBaseStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  color: '#fff',
  textDecoration: 'none',
  borderRadius: 10,
  padding: '14px 16px',
  border: '1px solid rgba(255,255,255,0.1)',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
};

/* ── Status & info ── */
const statusRowStyle = {
  display: 'flex',
  gap: 14,
  paddingTop: 8,
  borderTop: '1px solid rgba(255,255,255,0.06)',
};

const infoBoxStyle = {
  background: 'rgba(74,222,128,0.06)',
  border: '1px solid rgba(74,222,128,0.18)',
  borderRadius: 8,
  padding: '10px 12px',
  color: 'rgba(255,255,255,0.55)',
  fontSize: 11,
  lineHeight: 1.6,
};

const warningBoxStyle = {
  background: 'rgba(255,190,0,0.08)',
  border: '1px solid rgba(255,190,0,0.24)',
  borderRadius: 8,
  padding: '10px 12px',
  color: '#ffd37a',
  fontSize: 12,
  lineHeight: 1.6,
};

/* ── Debug / local fallback ── */
const separatorStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  color: 'rgba(255,255,255,0.25)',
  fontSize: 10,
  letterSpacing: 1,
  margin: '0 0 18px 0',
};

const separatorLineStyle = { flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' };

const debugBoxStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10,
  padding: '12px 14px',
  color: 'rgba(255,255,255,0.72)',
  fontSize: 12,
  lineHeight: 1.6,
  marginBottom: 14,
};

const labelStyle = {
  display: 'block',
  color: 'rgba(255,255,255,0.6)',
  fontSize: 11,
  letterSpacing: 1,
  marginBottom: 8,
  textTransform: 'uppercase',
};

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
};

const eyeBtnStyle = {
  position: 'absolute',
  right: 14,
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'none',
  border: 'none',
  color: 'rgba(255,255,255,0.45)',
  cursor: 'pointer',
  display: 'flex',
  padding: 0,
  transition: 'color 0.15s ease',
};

const submitBtnStyle = {
  width: '100%',
  padding: '14px',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  fontFamily: 'inherit',
  letterSpacing: 0.4,
  transition: 'all 0.15s ease',
};

const errorBoxStyle = {
  background: 'rgba(255,60,60,0.1)',
  border: '1px solid rgba(255,60,60,0.3)',
  borderRadius: 8,
  padding: '10px 14px',
  color: '#ff8a8a',
  fontSize: 13,
  marginTop: 18,
};

const footerStyle = {
  marginTop: 22,
  paddingTop: 18,
  borderTop: '1px solid rgba(255,255,255,0.08)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: 'rgba(255,255,255,0.34)',
  fontSize: 11,
};
